/* The controls both desks share.
 *
 * Nothing here fetches, and nothing here knows an endpoint: each of these takes
 * what it draws. That is what lets the same switch appear on a client's
 * registration settings and on an admin product row without either desk
 * learning about the other.
 *
 * The prototype carried two fixture constants through this file — a hardcoded
 * activation UUID and a staging registration host — and PageHead drew the
 * constant rather than the prop it was handed. Both are gone: a shared control
 * that has a customer's identifier baked into it is not shared, it is a
 * screenshot.
 */

import { createContext, useContext, useEffect, useRef, useState } from "react";
import type { ChangeEventHandler, MouseEvent, ReactNode } from "react";
import { Info } from "./kit.js";

const infoTipClosers = new Set<() => void>();

/** A section heading inside a page. */
export function SecLabel({
  children,
  /**
   * Why this section is here. Drawn as the circled i, not as a paragraph under
   * the heading: the heading is scanned, the explanation is asked for.
   */
  info,
}: {
  children: ReactNode;
  info?: ReactNode;
}): JSX.Element {
  const label = typeof children === "string" ? children : "this section";
  return (
    <div className="sec-row">
      <h2 className="sec">{children}</h2>
      {info !== undefined ? (
        <InfoTip popup label={label}>
          {info}
        </InfoTip>
      ) : null}
    </div>
  );
}

export type PageFreshnessValue = {
  readonly checkedAt: number | null;
  readonly refreshing: boolean;
  readonly onRefresh: () => void;
};

const PageFreshnessContext = createContext<PageFreshnessValue | null>(null);

/** Both desks read this so a page title and a chrome bar can share one clock. */
export function PageFreshnessProvider({
  value,
  children,
}: {
  value: PageFreshnessValue;
  children: ReactNode;
}): JSX.Element {
  return <PageFreshnessContext.Provider value={value}>{children}</PageFreshnessContext.Provider>;
}

export function usePageFreshness(): PageFreshnessValue | null {
  return useContext(PageFreshnessContext);
}

export function formatAsOf(at: number): string {
  return new Date(at).toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

/**
 * When this page was last asked for, and a way to ask again.
 *
 * Shaped like the Black Peak admin's dashboard bar: the time in mono, a quiet
 * Refresh, and a row of dots while a read is in flight. This part draws what
 * it is handed and does not fetch.
 */
export function PageFreshness({
  checkedAt,
  refreshing,
  onRefresh,
}: PageFreshnessValue): JSX.Element {
  return (
    <div className="freshness">
      <span className="freshness__asof">
        {checkedAt !== null ? `as of ${formatAsOf(checkedAt)}` : refreshing ? "Loading…" : ""}
      </span>
      <button type="button" className="btn btn-quiet btn-sm" disabled={refreshing} onClick={onRefresh}>
        Refresh
      </button>
    </div>
  );
}

export function FreshnessPulse(): JSX.Element {
  return (
    <div className="freshness-pulse" aria-hidden="true">
      {Array.from({ length: 8 }, (_, i) => (
        <span key={i} className="freshness-pulse__dot" style={{ animationDelay: `${String(i * 80)}ms` }} />
      ))}
    </div>
  );
}

/**
 * The top of a page: what it is, what it is for, and what it is about.
 *
 * `uuid` draws the record's own identifier. The prototype ignored this prop and
 * printed a fixture, which is why every screen in the pack showed the same
 * activation.
 */
export function PageHead({
  title,
  lede,
  uuid,
  /**
   * What the identifier under the title is called.
   *
   * THE LABEL WAS HARD-CODED AND THE PART IS SHARED, which meant every record
   * with an identifier was an activation as far as this header was concerned.
   * A client screen using it announced a company's id as "Activation UUID",
   * which is not a cosmetic slip: it tells a reader the uuid in front of them
   * belongs to a different thing than it does, and uuids are exactly what
   * people copy out of a header and paste somewhere that matters. Defaulted
   * rather than required so every existing caller keeps the words it had.
   */
  uuidLabel = "Activation ID",
  note,
  explain = "inline",
}: {
  title: string;
  lede?: string;
  uuid?: string;
  uuidLabel?: string;
  note?: string;
  /**
   * Where the lede and the note are drawn. "info" is the October hand-off's
   * page head: both behind an (i) on the title, because the Console's
   * explainer text is asked for rather than read. "inline" is the page head
   * as it was, and it stays the default until each screen is ported, since a
   * screen's checks read those sentences off the page and an (i) that has not
   * been opened does not show them.
   */
  explain?: "inline" | "info";
}): JSX.Element {
  const freshness = usePageFreshness();
  const behind = explain === "info" && (lede !== undefined || note !== undefined);
  const titleRow = (
    <h1>
      {title}
      {behind ? (
        <Info label={`About ${title}`}>
          {lede}
          {lede !== undefined && note !== undefined ? " " : null}
          {note}
        </Info>
      ) : null}
    </h1>
  );
  return (
    <header className="head">
      {freshness === null ? (
        titleRow
      ) : (
        <div className="head-bar">
          {titleRow}
          <PageFreshness {...freshness} />
        </div>
      )}
      {freshness !== null && freshness.refreshing ? <FreshnessPulse /> : null}
      {!behind && lede !== undefined ? <p className="lede">{lede}</p> : null}
      {uuid !== undefined ? (
        <dl className="meta">
          <div>
            <dt>{uuidLabel}</dt>
            <dd className="mono">{uuid}</dd>
          </div>
        </dl>
      ) : null}
      {/* The note is a line under the head, as the design draws it (TASKS §143,
          step 6), rather than the circled i it had become: what a page is
          reading and under whose access is read with the page, not asked for. */}
      {!behind && note !== undefined ? (
        <p className="help" style={{ margin: "16px 0 0" }}>
          {note}
        </p>
      ) : null}
    </header>
  );
}

/**
 * Admin's breadcrumbs, shared since the October hand-off so the Console's
 * drill-ins use the same ones: each ancestor is a link back, and the page you
 * are on is plain text.
 *
 * ONLY THE LAST ITEM IS WHERE YOU ARE. The prototype drew every item without a
 * `go` as the current page, which was true while the only such item was the
 * last. The Console's drill-ins have parents with nowhere to go — Kiosk above
 * Content, Game above Players, because those are sections of the rail and not
 * pages — and drawn that way a kiosk item announced three places as the page
 * it is on. A parent with no `go` is plain text in the ancestors' colour, not
 * a link and not current; the last item is current whether or not it has one.
 */
export function Crumbs({ items }: { items: ReadonlyArray<readonly [ReactNode, (() => void) | null | undefined]> }): JSX.Element {
  return (
    <p className="adm-crumbs">
      {items.map(([label, go], i) => {
        const here = i === items.length - 1;
        return (
          <span key={i} style={{ display: "contents" }}>
            {i > 0 ? <span aria-hidden="true">/</span> : null}
            {go && !here ? (
              <button type="button" className="crumb" onClick={go}>
                {label}
              </button>
            ) : here ? (
              <span className="crumb-here" aria-current="page">
                {label}
              </span>
            ) : (
              <span className="crumb-up">{label}</span>
            )}
          </span>
        );
      })}
    </p>
  );
}

/**
 * The one way back from a page opened out of a list, at the foot of the page
 * on the left (Gabe's ruling on the 12 Oct hand-off, audit A17).
 *
 * A PAGE SAYS WHERE IT IS AT THE TOP AND HOW TO LEAVE AT THE BOTTOM. The crumbs
 * above the title are the address, as on the Inventory product page; this is
 * the control a reader who has worked down to the end of the record reaches
 * for, so it sits where they finish rather than back where they started. One
 * of them: a page with a "← Clients" crumb and a top-right "Back to …" button
 * and this would be three ways to do one thing. `to` names the place, so the
 * button says "Back to Clients" and not a bare arrow.
 *
 * ONE BACK FOR EVERY PAGE, THE RECORD PAGES INCLUDED. Orders' and Leads'
 * record pages (#209) drew their own, a quiet button that is a link to the
 * list; this is that Back for them and for every other page, in their look.
 * `href` makes it a real link, so it opens the list in a new tab when asked
 * to (a modified click is the browser's); without one it is a button. desk-ui
 * knows no router, so a desk hands over the address its router built.
 */
export function BackLink({ to, onBack, href }: { to: string; onBack: () => void; href?: string }): JSX.Element {
  return (
    <p className="desk-back">
      {href !== undefined ? (
        <a
          className="btn btn-quiet"
          href={href}
          onClick={(event: MouseEvent<HTMLAnchorElement>) => {
            if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;
            event.preventDefault();
            onBack();
          }}
        >
          Back to {to}
        </a>
      ) : (
        <button type="button" className="btn btn-quiet" onClick={onBack}>
          Back to {to}
        </button>
      )}
    </p>
  );
}

/** Something the page wants said before the content under it is believed. */
export function NoticeBox({
  kind,
  title,
  children,
  alert = false,
}: {
  kind: "good" | "bad" | "amber" | "quiet";
  title?: ReactNode;
  children?: ReactNode;
  /**
   * Whether a reader who is not looking at this is interrupted by it.
   *
   * A REFUSAL IS NOT THE SAME ANNOUNCEMENT AS A RESULT. `role="status"` is
   * polite: a screen reader finishes what it is saying and mentions this when
   * it gets there, which is right for "saved" and wrong for "this was refused
   * and nothing happened". The second one has to arrive before the person acts
   * on the assumption that it worked, and that is `role="alert"`.
   *
   * Defaulted off rather than derived from `kind`, because the two questions
   * are different: a bad-tinted box that has been on the page since it loaded
   * is not an interruption, and a quiet one that appeared because a button was
   * pressed may well be.
   */
  alert?: boolean;
}): JSX.Element {
  return (
    <div className={`notice notice-${kind}`} role={alert ? "alert" : "status"}>
      {title !== undefined ? <h3>{title}</h3> : null}
      {children}
    </div>
  );
}

/** A numbered list of what is about to happen. */
export function StepsList({
  items,
  layout = "row",
}: {
  items: readonly string[];
  /** `row` is the three-across strip. `stack` is one sentence per line — the
   *  only readable shape in a narrow column, and the sidebar on a wide page. */
  layout?: "row" | "stack";
}): JSX.Element {
  return (
    <ol className={layout === "stack" ? "steps" : "steps steps-h"}>
      {items.map((text) => (
        <li key={text}>{text}</li>
      ))}
    </ol>
  );
}

/** A large choice with room for a sentence explaining it. */
export function Pick({
  type,
  name,
  title,
  desc,
  on,
  onPick,
}: {
  type: "radio" | "checkbox";
  name: string;
  title: string;
  desc?: string;
  on: boolean;
  onPick: ChangeEventHandler<HTMLInputElement>;
}): JSX.Element {
  return (
    <label className={on ? "pick pick-on" : "pick"}>
      <input type={type} name={name} checked={on} onChange={onPick} />
      <span className="pick-b">
        <span className="pick-t">{title}</span>
        {desc !== undefined ? <span className="pick-d">{desc}</span> : null}
      </span>
    </label>
  );
}

/** A labelled field, with room for the sentence that stops it being misread. */
export function FieldRow({
  label,
  help,
  children,
}: {
  label: string;
  help?: string;
  children?: ReactNode;
}): JSX.Element {
  return (
    <label className="field">
      <span className="label">{label}</span>
      {children}
      {help !== undefined ? <span className="help help-tight">{help}</span> : null}
    </label>
  );
}

/** The bar that follows a long form down the page carrying its actions. */
export function StickyBar({
  children,
  note,
  warn,
}: {
  children?: ReactNode;
  note?: string;
  warn?: string;
}): JSX.Element {
  return (
    <div className="sticky">
      <div className="acts acts-flush">{children}</div>
      {warn !== undefined ? <p className="warn">{warn}</p> : null}
      {note !== undefined ? <p className="note">{note}</p> : null}
    </div>
  );
}

/** Term and definition pairs: the shape most of a record is read in. */
export function DefList({ rows }: { rows: ReadonlyArray<readonly [string, ReactNode]> }): JSX.Element {
  return (
    <dl className="dl">
      {rows.map(([term, value]) => (
        <div key={term}>
          <dt>{term}</dt>
          <dd>{value}</dd>
        </div>
      ))}
    </dl>
  );
}

/** Where a QR code goes. The code itself is drawn by whoever has the payload. */
export function QrTile({ label, children }: { label: string; children?: ReactNode }): JSX.Element {
  return (
    <div className="qr" role="img" aria-label={label}>
      {children ?? "QR"}
    </div>
  );
}

/** One of a set of things that can each be on or off. */
export function Chip({
  label,
  on,
  onPick,
  disabled = false,
  title,
}: {
  label: string;
  on: boolean;
  onPick: ChangeEventHandler<HTMLInputElement>;
  /** A chip that cannot change now, said by the control rather than by a click that does nothing. */
  disabled?: boolean;
  /** Why, when it is disabled. */
  title?: string;
}): JSX.Element {
  return (
    <label className={[on ? "chip chip-on" : "chip", disabled ? "is-off" : undefined].filter(Boolean).join(" ")} title={title}>
      <input type="checkbox" checked={on} onChange={onPick} disabled={disabled} />
      <span>{label}</span>
    </label>
  );
}

/** A small set of alternatives where exactly one is chosen. */
export function Seg2<Option extends string>({
  label,
  value,
  options,
  onPick,
}: {
  label: string;
  value: Option;
  options: readonly Option[];
  onPick: (option: Option) => void;
}): JSX.Element {
  return (
    <div className="seg2" role="group" aria-label={label}>
      {options.map((option) => (
        <button
          type="button"
          key={option}
          aria-pressed={value === option}
          onClick={() => onPick(option)}
        >
          {option}
        </button>
      ))}
    </div>
  );
}

/** A choice drawn as a full-width row, for lists of them. */
export function RowPick({
  type,
  name,
  title,
  desc,
  on,
  onPick,
  value,
  disabled,
}: {
  type: "radio" | "checkbox";
  name: string;
  title: string;
  desc?: string;
  on: boolean;
  onPick: ChangeEventHandler<HTMLInputElement>;
  /** The input's own value, for a form or a reader that names a radio by it. */
  value?: string;
  disabled?: boolean;
}): JSX.Element {
  return (
    <label className={on ? "row-pick row-on" : "row-pick"}>
      <input type={type} name={name} checked={on} onChange={onPick} value={value} disabled={disabled} />
      <span>
        <span className="t">{title}</span>
        {desc !== undefined ? <span className="d">{desc}</span> : null}
      </span>
    </label>
  );
}

/** On or off, with the state said in words as well as drawn. */
export function Switch({
  on,
  onFlip,
  label,
}: {
  on: boolean;
  onFlip: ChangeEventHandler<HTMLInputElement>;
  label?: string;
}): JSX.Element {
  return (
    <label className="switch">
      <input type="checkbox" checked={on} onChange={onFlip} />
      <span className="sw-t" />
      <span className="sw-l">{label ?? (on ? "On" : "Off")}</span>
    </label>
  );
}

/** A short piece of state on a row. */
export function Tag({ children, off = false }: { children: ReactNode; off?: boolean }): JSX.Element {
  return <span className={off ? "tag tag-off" : "tag"}>{children}</span>;
}

/**
 * A checkbox with its label, for the ordinary case.
 *
 * The label is a node rather than a string because a row that is chosen by
 * ticking it usually wants its position in the label — "01" set in the mono
 * face — and a checkbox whose only label is a bare number is unreadable to
 * anybody listening to it rather than looking.
 */
export function Check({
  on,
  onFlip,
  label,
  disabled = false,
}: {
  on: boolean;
  onFlip: ChangeEventHandler<HTMLInputElement>;
  label: ReactNode;
  /** A tick that cannot change now, said by the control rather than by a click that does nothing. */
  disabled?: boolean;
}): JSX.Element {
  return (
    <label className={disabled ? "check is-off" : "check"}>
      <input type="checkbox" checked={on} onChange={onFlip} disabled={disabled} aria-disabled={disabled || undefined} />
      <span>{label}</span>
    </label>
  );
}

/**
 * The paths, from the handoff's own console-parts.jsx, character for character.
 *
 * `close` is the one that is not the design's: the pack has no close icon
 * because the pack's rehearsal banner has no close button. It is drawn in the
 * same idiom — one path, 24-unit box, stroked rather than filled — so that it
 * sits at the same weight as the four beside it rather than reading as an
 * import from somewhere else.
 */
const MODE_ICONS = {
  light:
    "M12 4V2m0 20v-2m8-8h2M2 12h2m13.66-5.66 1.41-1.41M4.93 19.07l1.41-1.41m11.32 0 1.41 1.41M4.93 4.93l1.41 1.41M12 8a4 4 0 1 1 0 8 4 4 0 0 1 0-8z",
  dark: "M20 14.5A8.5 8.5 0 0 1 9.5 4a8.5 8.5 0 1 0 10.5 10.5z",
  panel: "M4 5h16v14H4zM9.5 5v14",
  menu: "M4 7h16M4 12h16M4 17h16",
  close: "M6.5 6.5l11 11m0-11l-11 11",
  // The Admin bar's two controls that fold to a mark when the bar is narrow
  // (admin-desk.css): Refresh, and the lookup.
  refresh: "M19.5 12a7.5 7.5 0 1 1-1.75-4.82M18 3v4.2h-4.2",
  search: "M10.5 4.5a6 6 0 1 1 0 12 6 6 0 0 1 0-12zM15 15l4.5 4.5",
} as const;

/** The marks the shell needs: two appearances, a panel, a menu, a close, Refresh and the lookup. */
export function ModeIcon({ kind }: { kind: keyof typeof MODE_ICONS }): JSX.Element {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d={MODE_ICONS[kind]}
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/**
 * A short explanation of the control it sits beside, folded away until asked for.
 *
 * NOT A TOOLTIP, AND THAT IS THE ONLY INTERESTING DECISION IN IT. The obvious
 * build is a title attribute or a panel on hover, and both fail the same way:
 * hover is not a thing a finger can do, so on the tablet half of this estate the
 * explanation would simply not exist. A native `title` is worse again, because
 * it is invisible until a second of stillness, cannot be styled, and is read out
 * by some screen readers and not others, so it is a help text whose presence
 * depends on the reader's software.
 *
 * A button that toggles a paragraph works with a mouse, a finger and a keyboard,
 * announces itself as expanded or collapsed, and the paragraph is ordinary text
 * in the document rather than a thing a browser draws over the page. It costs a
 * line of state and a click where a tooltip costs none, and that is the whole of
 * what is being traded.
 *
 * `label` names what is being explained rather than the mark: "what a starting
 * point is", not "info". A rail of identical buttons all called "More
 * information" is a rail nobody listening to it can navigate, and the label is
 * the only thing telling them apart.
 */
export function InfoTip({
  label,
  children,
  /**
   * A panel over the page rather than a paragraph that pushes what follows.
   * Section headings use this so opening the i does not move the section.
   */
  popup = false,
}: {
  label: string;
  children: ReactNode;
  popup?: boolean;
}): JSX.Element {
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    const close = (): void => setOpen(false);
    infoTipClosers.add(close);
    return () => {
      infoTipClosers.delete(close);
    };
  }, []);

  useEffect(() => {
    if (!open) return;
    const onDoc = (event: Event): void => {
      if (root.current?.contains(event.target as Node)) return;
      setOpen(false);
    };
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("click", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("click", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <span ref={root} className={popup ? "infotip infotip--popup" : "infotip"}>
      <button
        type="button"
        className="infotip__ask"
        aria-expanded={open}
        aria-label={open ? `Hide ${label}` : `Explain ${label}`}
        onClick={() => {
          if (open) {
            setOpen(false);
            return;
          }
          for (const close of infoTipClosers) close();
          setOpen(true);
        }}
      >
        {/* Drawn rather than typed. A letter i in the body face is a letter in a
            circle at whatever size the surrounding text happens to be, and it
            inherits the italic and the letter-spacing of anything it is nested
            in. The 24-unit stroked box is the idiom every other mark here uses. */}
        <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.6" />
          <path
            d="M12 11v5.5"
            stroke="currentColor"
            strokeWidth="1.6"
            strokeLinecap="round"
          />
          <circle cx="12" cy="7.75" r="1.05" fill="currentColor" />
        </svg>
      </button>
      {/* Kept in the document when shut, so a check that reads the page still
          finds the words and a screen reader can be pointed at them. `hidden`
          is the browser's own hide, not a class a theme can miss. */}
      <span className="infotip__says" hidden={!open}>
        {children}
      </span>
    </span>
  );
}

/**
 * The strip across the top of a desk whose numbers are not somebody's event.
 *
 * THE WORDING IS NOT THIS COMPONENT'S AND MUST NOT BECOME IT. The three parts
 * arrive as props because the sentence is a statement about a particular
 * service — xp/console/src/build/server.ts writes it, avoids the word staging
 * on purpose, and names the server afterwards for whoever does know the word.
 * A shared control that composed its own sentence would be a shared control
 * telling a client something the desk it is drawn on never decided.
 *
 * ================================ THE CLOSE BUTTON, AND WHAT IT COST TO ADD
 *
 * There was no close button here until now, and the absence was argued rather
 * than overlooked. xp/app/src/server/ServerMark.tsx and dev/Watermark.tsx both
 * state the rule in one line — "a mark you can turn off is a mark that is off
 * in the screenshot somebody sends you" — and furniture.css called this rule
 * load-bearing.
 *
 * THAT ARGUMENT IS ABOUT PERSISTENCE, AND IT IS ONLY ABOUT PERSISTENCE. What it
 * defends against is a reader who turned the mark off last Tuesday and has been
 * reading rehearsal numbers as their event ever since, and a screenshot of a
 * console that looks like production because the person who took it had put the
 * warning away weeks ago. Both of those need the dismissal to survive
 * something: a reload, a new tab, a session. Neither of them is a person who
 * pressed a button thirty seconds ago and can see the page they pressed it on.
 *
 * SO THE STATE IS REACT'S AND NOTHING WRITES IT DOWN. Not localStorage, not
 * sessionStorage, not a cookie, not a query parameter. Every fresh load of the
 * document draws the strip again, which means the honest version of the old
 * rule survives intact: nobody can arrive at this screen without being told
 * what it is. A person who has been told, and has said so, gets their top
 * eleven pixels back for as long as they stay on the page.
 *
 * IT IS ALSO PER MOUNT RATHER THAN PER TAB, which is a shade stricter than the
 * ask and is deliberately not corrected. A module-level flag would keep the
 * dismissal across an unmount — signing out and back in, say — and there is no
 * reason to extend it that far: a new sign-in is exactly the moment somebody
 * decides again whether they are looking at the right service. The bias in both
 * directions is toward showing the warning more often, never less.
 *
 * A REAL BUTTON WITH A REAL LABEL. Not a bare glyph, and not a div with a click
 * handler. It is in the tab order, it takes the desk's focus ring from
 * furniture.css, and it is announced as "Hide this notice" rather than as the
 * multiplication sign, because the reader most likely to want a banner out of
 * the way is the one paying the highest price for it being there.
 */
export function RehearsalMark({
  label,
  sentence,
  server,
  className,
}: {
  /** The word set apart, or null where the whole mark is one sentence. */
  label: string | null;
  sentence: string;
  /** Which server, in brackets, or null where there is none to name. */
  server: string | null;
  /** A desk's own class on the same element, for the desk's own selectors. */
  className?: string;
}): JSX.Element | null {
  const [hidden, setHidden] = useState(false);
  if (hidden) return null;

  return (
    <div className={className === undefined ? "mark" : `mark ${className}`} role="status">
      {label === null ? null : <b>{label}</b>}
      <span>
        {sentence}
        {server === null ? null : (
          <>
            {" "}
            <span className="srv">{server}</span>
          </>
        )}
      </span>
      <button
        type="button"
        className="mark-x"
        aria-label="Hide this notice"
        onClick={() => setHidden(true)}
      >
        <ModeIcon kind="close" />
      </button>
    </div>
  );
}

/** One line of a settings page: what it is, what it does, and its control. */
export function Setting({
  label,
  desc,
  info,
  children,
}: {
  label: string;
  desc?: string;
  /**
   * Why this row is here. Drawn as the circled i beside the label, not as a
   * paragraph under the control: the row is scanned, the explanation is asked
   * for. Same shape SecLabel uses on a heading.
   *
   * THE OCTOBER HAND-OFF PUTS IT INSIDE `.k`, as the kit's (i) — the one the
   * page head, the field labels and the section headings wear — rather than
   * the older InfoTip beside it. One mark for "ask me" across a page, drawn by
   * one part. Phrasing content only: the pop is a span inside the label's
   * paragraph, so a caller hands in words, not paragraphs.
   */
  info?: ReactNode;
  children?: ReactNode;
}): JSX.Element {
  return (
    <div className="setting">
      <div>
        <div className="setting-k">
          <p className="k">
            {label}
            {info !== undefined ? <Info label={`About ${label}`}>{info}</Info> : null}
          </p>
        </div>
        {desc !== undefined ? <p className="d">{desc}</p> : null}
      </div>
      <div>{children}</div>
    </div>
  );
}

/** How far through a sequence of steps somebody is, and what is still owed. */
export function Wizard({
  steps,
  at,
  onGo,
  note,
  marks,
  tabs = true,
}: {
  steps: readonly string[];
  at: number;
  onGo?: (index: number) => void;
  note?: string;
  marks?: ReadonlyArray<"done" | "todo" | undefined>;
  tabs?: boolean;
}): JSX.Element {
  const show = tabs && onGo !== undefined;
  return (
    <div className="wiz">
      <div className="wiz-bar" aria-hidden="true">
        {steps.map((step, i) => (
          <i key={step} className={i <= at ? "on" : undefined} />
        ))}
      </div>
      <div className="wiz-head">
        <span className="wiz-n">
          Step {at + 1} of {steps.length}
        </span>
        <span className="wiz-t">{steps[at]}</span>
      </div>
      {show ? (
        <div className="wiz-tabs">
          {steps.map((step, i) => {
            const mark = marks?.[i] ?? (i < at ? "done" : undefined);
            return (
              <button
                type="button"
                key={step}
                className={mark}
                aria-current={i === at ? "step" : undefined}
                onClick={() => onGo(i)}
              >
                {i + 1}. {step}
                {mark === "todo" ? " · needed" : ""}
              </button>
            );
          })}
        </div>
      ) : null}
      {note !== undefined ? <p className="wiz-note">{note}</p> : null}
    </div>
  );
}
